import {Injectable} from '@angular/core';
import {DiaryEvent, DiaryEventGeneric} from '@shared/models/diary.model';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {IdName} from '@core/src/public-api';
import {weekDaysBinary, weekDaysNamesMin} from '@shared/constants/date.constants';
import * as moment from 'moment';
import {TranslateService} from '@ngx-translate/core';
import {Medication} from '@shared/models/medication.model';
import {diaryEventTypeCodes} from '@shared/constants/diary.constants';

@Injectable({
  providedIn: 'root'
})
export class DiaryService {

  constructor(
    private http: HttpClient,
    private translateService: TranslateService
  ) {
  }

  createEvent(model: DiaryEvent): Promise<void> {
    const url = `/api/v1/calendar/event`;
    return this.http.post<void>(url, model).toPromise();
  }

  getEvent(eventId: string): Promise<DiaryEvent> {
    const url = `/api/v1/calendar/event/${eventId}`;
    return this.http.get<DiaryEvent>(url).toPromise();
  }

  getEvents(initDate?: number, endDate?: number): Promise<DiaryEvent[]> {
    const url = '/api/v1/calendar/event';
    let httpParams = new HttpParams();
    if (initDate) {
      httpParams = httpParams.set('initDate', initDate.toString());
    }

    if (endDate) {
      httpParams = httpParams.set('endDate', endDate.toString());
    }

    return this.http.get<DiaryEvent[]>(url, {params: httpParams}).toPromise();
  }

  getEventsPaged(page: number, size: number, initDate?: number, endDate?: number): Promise<HttpResponse<DiaryEvent[]>> {
    const url = '/api/v1/calendar/event/paged';
    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('paged', 'true');

    if (initDate) {
      httpParams = httpParams.set('initDate', initDate.toString());
    }

    if (endDate) {
      httpParams = httpParams.set('endDate', endDate.toString());
    }

    return this.http.get<DiaryEvent[]>(url, {params: httpParams, observe: 'response'}).toPromise();
  }

  getMedication(page: number, size: number, initDate: number, endDate: number, active?: boolean): Promise<Medication[]> {
    const url = '/api/v1/medication';
    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('initDate', initDate.toString())
      .set('endDate', endDate.toString())
      .set('types', diaryEventTypeCodes.MEDICATION);

    if (active) {
      httpParams = httpParams.set('active', active.toString());
    }

    return this.http.get<Medication[]>(url, {params: httpParams}).toPromise();
  }

  changeEventState(state: IdName, eventId: string): Promise<DiaryEvent> {
    const url = `/api/v1/calendar/event/${eventId}/change/status`;
    return this.http.put<DiaryEvent>(url, state).toPromise();
  }

  deleteEvent(eventId: string): Promise<void> {
    const url = `/api/v1/calendar/event/${eventId}`;
    return this.http.delete<void>(url).toPromise();
  }

  deleteSerieEvent(eventId: string): Promise<void> {
    const url = `/api/v1/calendar/event/${eventId}/serie`;
    return this.http.delete<void>(url).toPromise();
  }

  updateEvent(model: DiaryEventGeneric): Promise<DiaryEvent> {
    const url = `/api/v1/calendar/event/${model.id}`;
    return this.http.put<DiaryEvent>(url, model).toPromise();
  }

  updateSerieEvent(model: DiaryEventGeneric): Promise<DiaryEvent> {
    const url = `/api/v1/calendar/event/${model.id}/serie`;
    return this.http.put<DiaryEvent>(url, model).toPromise();
  }

  // Functions

  /**
   * Adds the binary values of each day-number of the array, and then fill with 0's to get a 7 digits number.
   *
   * Example:
   * let array = [1, 2, 5]; //Monday, Tuesday, Friday
   * weekDays = weekDaysFromArray(array); // Returns 1100100
   */
  weekDaysFromArray(array: number[]): string {
    return array.map(n => parseInt(weekDaysBinary[n])).reduce((acc, cur) => acc + cur).toString().padStart(7, '0');
  }

  weekDaysToArray(weekDays: string): number[] {
    const weekDaysArray = [];
    weekDays?.split('').forEach((item, index) => {
      if (item == '1') {
        weekDaysArray.push(index + 1);
      }
    });

    return weekDaysArray;
  }

  weekDaysFromDate(date: number): string {
    return weekDaysBinary[moment(date).isoWeekday()];
  }

  weekDaysToString(weekDays: string): string {
    const weekDaysArray = [];
    weekDays?.split('').forEach((item, index) => {
      if (item == '1') {
        weekDaysArray.push(this.translateService.instant(weekDaysNamesMin[index + 1]));
      }
    });

    return weekDaysArray.join(', ');
  }
}
